import React, { ReactNode } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { PublicClientApplication, EventType, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './msalConfig';
import { AuthStart } from './AuthStart';
import { AuthEnd } from './AuthEnd';
import { isInTeams, teamsSilentLogin, teamsInteractiveLogin } from './teamsAuth';

export const msalInstance = new PublicClientApplication(msalConfig);

// Set the first signed-in account as active
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const result = event.payload as AuthenticationResult;
    msalInstance.setActiveAccount(result.account);
  }
});

const LoginScreen: React.FC = () => {
  const { instance } = useMsal();
  const [teamsBusy, setTeamsBusy] = React.useState(false);
  const [teamsError, setTeamsError] = React.useState('');

  // Inside Microsoft Teams, try a fully silent sign-in first — the user is
  // already signed into Teams with the same M365 account, so this usually
  // succeeds with no UI at all. Outside Teams this is a no-op.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (await isInTeams()) {
        if (cancelled) return;
        setTeamsBusy(true);
        const ok = await teamsSilentLogin(instance);
        if (!ok && !cancelled) setTeamsBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance]);
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#faf7f2',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #d9d4cb',
          borderRadius: 8,
          padding: '48px 56px',
          textAlign: 'center',
          maxWidth: 420,
        }}
      >
        <div
          style={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize: 28,
            color: '#1e4d3a',
            letterSpacing: '-0.5px',
          }}
        >
          HelpDesk<span style={{ color: '#7a9b7e' }}>Pro</span>
        </div>
        <div style={{ marginTop: 8, color: '#6b6b6b', fontSize: 13 }}>
          Society Brands &middot; IT Helpdesk
        </div>
        <button
          onClick={() => {
            (async () => {
              try {
                setTeamsError('');
                if (await isInTeams()) {
                  setTeamsBusy(true);
                  await teamsInteractiveLogin(instance);
                } else {
                  await instance.loginRedirect(loginRequest);
                }
              } catch (err) {
                console.error(err);
                setTeamsBusy(false);
                setTeamsError('Sign-in failed. Please try again, or contact IT.');
              }
            })();
          }}
          style={{
            marginTop: 32,
            padding: '10px 24px',
            background: '#1e4d3a',
            color: '#ffffff',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Sign in with Microsoft 365
        </button>
        <div style={{ marginTop: 16, fontSize: 12, color: '#9a9a9a' }}>
          Use your @societybrands.com account
        </div>

        {(teamsBusy || teamsError) && (
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: teamsError ? '#c4533a' : '#6b6b6b',
            }}
          >
            {teamsError || 'Signing you in with your Teams account\u2026'}
          </div>
        )}

        {/* Help text — what to do if sign-in fails */}
        <div
          style={{
            marginTop: 28,
            padding: '14px 16px',
            background: '#faf7f2',
            border: '1px solid #e8e1d4',
            borderRadius: 6,
            textAlign: 'left',
            fontSize: 12,
            color: '#5a5a5a',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1e4d3a', marginBottom: 4 }}>
            Can't sign in?
          </div>
          Reach out to IT and we'll help get you in.
        </div>
      </div>
    </div>
  );
};

interface Props {
  children: ReactNode;
}

export const AuthProvider: React.FC<Props> = ({ children }) => {
  // The Teams sign-in popup loads these two routes; they must render without
  // any authentication gate (the popup's whole job is to create the session).
  const path = window.location.pathname;
  if (path === '/auth-start') return <AuthStart />;
  if (path === '/auth-end') return <AuthEnd />;

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
};
